/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createOperationCall, OperationHandler } from 'src/core/services/api/createOperationCall';
import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { TrainingGetStatusSwagger } from 'src/luis/core/services/training/trainingSwagger';
import { TrainingModes } from 'src/luis/types/trainingTypes';

type TrainingPostSwagger = { status: 'UpToDate' | 'Queued' };

/**
 * Service that handles the training API calls.
 */
export const getTrainingService = createService(({ context, getEnvironment }) => {
    const { apiUrl: v3ApiUrl, isProduction } = getEnvironment({
        apiVersion: 'v3.0-preview'
    });

    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    /**
     * Issues a training request and starts polling for status.
     * If the training was already issued, continue to poll the
     * service for status.
     *
     * @param trainingStatusHandler The polling handler to create the async calls.
     * @returns True if the application was already up to date and false otherwise.
     */
    const train = async ({
        mode = 'Standard',
        trainingStatusHandler
    }: {
        mode?: TrainingModes;
        trainingStatusHandler: OperationHandler<TrainingGetStatusSwagger>;
    }): Promise<boolean> => {
        const getBaseUrl = () =>
            urlBuilder()
                .baseUrl(v3ApiUrl)
                .app(context.appId)
                .version(context.versionId)
                .resource('train')
                .queryParam('mode', mode);

        const statusUrl = urlBuilder()
            .baseUrl(v3ApiUrl)
            .app(context.appId)
            .version(context.versionId)
            .resource('train');

        let trainingStatus: TrainingPostSwagger;
        let trainingPostHeaders: Record<string, string>;
        let isPreviousTrainingInProgress: boolean;

        try {
            const { data, headers } = await httpService.post<TrainingPostSwagger>(getBaseUrl().build(), {}, apiOptions);
            trainingStatus = data;
            trainingPostHeaders = <Record<string, string>>headers;
        } catch (error) {
            const errorCode = (<Error>error).name;

            /**
             * If a training request had already occurred before
             * then mark the previous training as in progress.
             */
            if (errorCode === LuisConstants.HttpResponseCodes.TrainingInProgress) {
                isPreviousTrainingInProgress = true;
            } else {
                throw error;
            }
        }

        if (isPreviousTrainingInProgress || trainingStatus.status !== 'UpToDate') {
            const url = trainingPostHeaders ? trainingPostHeaders[LuisConstants.OperationHeaders.LocationHeader] : statusUrl.build();

            await createOperationCall(url, apiOptions, trainingStatusHandler);

            return false;
        }

        return true;
    };

    return { train };
});
